import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ProductOptionFieldRenderer from '../components/ProductOptionFieldRenderer';

/**
 * Demo page to showcase all Product Option Field types
 * This helps admins and developers see how fields will render for customers
 */
const ProductOptionFieldsDemo = () => {
  const [formData, setFormData] = useState({});

  const handleChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
  };

  const demoFields = [
    {
      id: '1',
      type: 'text',
      name: 'custom_text',
      label: 'Custom Text',
      placeholder: 'Enter your custom text',
      help_text: 'This is a simple text input field',
      required: true
    },
    {
      id: '2',
      type: 'time',
      name: 'delivery_time',
      label: 'Preferred Delivery Time',
      help_text: 'Select your preferred time slot',
      required: true
    },
    {
      id: '3',
      type: 'button-select',
      name: 'size_button',
      label: 'Select Size (Button Style)',
      help_text: 'Choose your preferred size',
      price_impact: true,
      options: [
        { label: 'Small', value: 's', default_price: 100 },
        { label: 'Medium', value: 'm', default_price: 150 },
        { label: 'Large', value: 'l', default_price: 200 },
        { label: 'X-Large', value: 'xl', default_price: 250 },
        { label: 'XX-Large', value: 'xxl', default_price: 300 }
      ],
      required: true
    },
    {
      id: '4',
      type: 'image-cards',
      name: 'color_image',
      label: 'Select Color (Image Cards)',
      help_text: 'Choose your favorite color variant',
      price_impact: true,
      options: [
        { 
          label: 'Red', 
          value: 'red', 
          default_price: 0,
          image_url: 'https://via.placeholder.com/200/ff0000/ffffff?text=Red'
        },
        { 
          label: 'Blue', 
          value: 'blue', 
          default_price: 50,
          image_url: 'https://via.placeholder.com/200/0000ff/ffffff?text=Blue'
        },
        { 
          label: 'Green', 
          value: 'green', 
          default_price: 50,
          image_url: 'https://via.placeholder.com/200/00ff00/000000?text=Green'
        },
        { 
          label: 'Yellow', 
          value: 'yellow', 
          default_price: 75,
          image_url: 'https://via.placeholder.com/200/ffff00/000000?text=Yellow'
        }
      ],
      required: true
    },
    {
      id: '5',
      type: 'gallery',
      name: 'pattern_gallery',
      label: 'Select Pattern (Gallery)',
      help_text: 'Choose your preferred pattern',
      options: [
        { 
          label: 'Dots', 
          value: 'dots',
          image_url: 'https://via.placeholder.com/100/000000/ffffff?text=Dots'
        },
        { 
          label: 'Stripes', 
          value: 'stripes',
          image_url: 'https://via.placeholder.com/100/333333/ffffff?text=Stripes'
        },
        { 
          label: 'Solid', 
          value: 'solid',
          image_url: 'https://via.placeholder.com/100/666666/ffffff?text=Solid'
        },
        { 
          label: 'Floral', 
          value: 'floral',
          image_url: 'https://via.placeholder.com/100/999999/ffffff?text=Floral'
        },
        { 
          label: 'Geometric', 
          value: 'geometric',
          image_url: 'https://via.placeholder.com/100/cccccc/000000?text=Geo'
        },
        { 
          label: 'Abstract', 
          value: 'abstract',
          image_url: 'https://via.placeholder.com/100/eeeeee/000000?text=Abstract'
        }
      ],
      required: false
    },
    {
      id: '6',
      type: 'radio',
      name: 'material',
      label: 'Select Material (Traditional Radio)',
      price_impact: true,
      options: [
        { label: 'Cotton', value: 'cotton', default_price: 0 },
        { label: 'Silk', value: 'silk', default_price: 200 },
        { label: 'Wool', value: 'wool', default_price: 150 }
      ],
      required: true
    },
    {
      id: '7',
      type: 'checkbox',
      name: 'add_ons',
      label: 'Add-ons (Multiple Selection)',
      help_text: 'Select multiple add-ons',
      price_impact: true,
      options: [
        { label: 'Gift Wrapping', value: 'gift_wrap', default_price: 50 },
        { label: 'Express Delivery', value: 'express', default_price: 100 },
        { label: 'Insurance', value: 'insurance', default_price: 75 }
      ]
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold">Product Option Fields - Demo</h1>
          <p className="text-gray-600 mt-2">
            Preview of all available field types for product customization
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Customer-Facing Form Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {demoFields.map((field) => (
              <ProductOptionFieldRenderer
                key={field.id}
                field={field}
                value={formData[field.name]}
                onChange={handleChange}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Selected Values (Live Preview)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-auto">
              {JSON.stringify(formData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProductOptionFieldsDemo;
